import classnames from "classnames";
import { getMonth, getYear, isFuture } from "date-fns";
import range from "lodash/range";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useFormContext, useWatch } from "react-hook-form";

import { getFormattedDate, isValidDate } from "../../../helpers";
import { FormControl } from "../Form";
import RHFController from "../Form/RHFController";
import FormHelper from "../FormHelper";
import FormInput from "../FormInput";
import Label from "../Label";

import ErrorBoundary from "../../../utils/ErrorBoundary";
import "./dateInput.scss";

const DateInput = ({
  name,
  label,
  options,
  helperText,
  className,
  isGroupFieldInput,
  groupFieldIndex,
  groupFieldName,
  datePickerOptions,
  shouldRevalidate,
}) => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useFormContext();
  const currentValue = useWatch({
    control,
    name,
  });
  const [isFocused, setIsFocused] = useState(false);

  const formattedValue = getFormattedDate(currentValue);
  const validatedCurrentValue = isValidDate(formattedValue)
    ? formattedValue
    : null;

  const datePickerRequired = datePickerOptions?.required;
  let hasValue = typeof currentValue !== "undefined" && currentValue !== "";
  let currentError = errors[name];

  const formattedInputClassName = isGroupFieldInput
    ? name.replace(
        `${groupFieldName}[${groupFieldIndex}].`,
        `${groupFieldName}${groupFieldIndex}-`
      )
    : name;

  if (isGroupFieldInput && errors[groupFieldName]?.[groupFieldIndex]) {
    currentError =
      errors[groupFieldName]?.[groupFieldIndex]?.[
        name.replace(`${groupFieldName}[${groupFieldIndex}].`, "")
      ];
  }

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e) => {
    setIsFocused(false);

    let selectedValue;

    if (e) {
      if (e.target.value !== "") {
        const value = e.target.value;
        const isValid = isValidDate(value);
        selectedValue = isValid ? value : validatedCurrentValue;
      } else {
        selectedValue = null;
      }
    } else {
      selectedValue = validatedCurrentValue;
    }

    hasValue = !!selectedValue;
  };

  const years = range(1990, getYear(new Date()) + 50, 1);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const selectOptions = (options) => {
    if (options.length <= 0) return;

    return (
      <>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </>
    );
  };

  useEffect(() => {
    if (!isValidDate(currentValue)) {
      setValue(name, "");
    }

    if (
      isGroupFieldInput &&
      shouldRevalidate &&
      currentValue?.trim() === "" &&
      datePickerRequired
    ) {
      setError(name, { type: "required", message: "Required field" });
    }

    if (isFuture(new Date(currentValue))) {
      setError(name, { type: "future", message: "Enter a past date" });
    }
    return () => {
      clearErrors(name);
    };
  }, [
    currentValue,
    isGroupFieldInput,
    shouldRevalidate,
    name,
    setError,
    clearErrors,
    setValue,
    datePickerRequired,
  ]);

  return (
    <ErrorBoundary fallback={<div>Oops.</div>}>
      <FormControl>
        <FormInput
          type="date"
          className={classnames(className, {
            "has-value": hasValue,
            "has-error": currentError && shouldRevalidate,
            "is-active": isFocused,
            "is-disabled": options?.disabled,
          })}
        >
          <Label
            htmlFor={name}
            text={label}
            required={options?.required}
            showRequiredAsterisk={!isGroupFieldInput}
          />
          <RHFController
            {...{
              register,
              name,
              value: currentValue,
              rules: { options },
              render: ({ name, value, onChange }) => {
                const dateArray = value.includes("/")
                  ? value.split("/")
                  : value.split("-");

                const dateVal = value.includes("/")
                  ? new Date(
                      dateArray[2],
                      (parseInt(dateArray[0]) + 11) % 12,
                      dateArray[1]
                    )
                  : new Date(
                      dateArray[0],
                      (parseInt(dateArray[1]) + 11) % 12,
                      dateArray[2]
                    );
                return (
                  <ReactDatePicker
                    {...datePickerOptions}
                    id={name}
                    name={name}
                    placeholderText={options?.placeholder}
                    formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
                    onChange={(e) => {
                      const formattedDate = getFormattedDate(e);
                      const val =
                        e && isValidDate(formattedDate)
                          ? formattedDate
                          : validatedCurrentValue;
                      return onChange(val);
                    }}
                    selected={isValidDate(value) ? dateVal : null} // Convert formatted value (MM/DD?YYYY) to Date obj
                    shouldCloseOnSelect={true}
                    aria-invalid={!!currentError}
                    disabled={options?.disabled}
                    readOnly={options?.readOnly}
                    maxDate={options?.maxDate}
                    showPopperArrow={false}
                    fixedHeight
                    dropdownMode="select"
                    popperPlacement="top"
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onCalendarClose={handleBlur}
                    className={`text-input--${formattedInputClassName}`}
                    renderCustomHeader={({ date, changeYear, changeMonth }) => (
                      <div className="react-datepicker__header__dropdown react-datepicker__header__dropdown--select">
                        <div className="react-datepicker__month-dropdown-container react-datepicker__month-dropdown-container--select">
                          <select
                            className="react-datepicker__month-select"
                            value={months[getMonth(date)]}
                            onChange={({ target: { value } }) =>
                              changeMonth(months.indexOf(value))
                            }
                            aria-label="Choose month"
                          >
                            {selectOptions(months)}
                          </select>
                        </div>

                        <div className="react-datepicker__year-dropdown-container react-datepicker__year-dropdown-container--select">
                          <select
                            className="react-datepicker__year-select"
                            value={getYear(date)}
                            onChange={({ target: { value } }) =>
                              changeYear(value)
                            }
                            aria-label="Choose year"
                          >
                            {selectOptions(years)}
                          </select>
                        </div>
                      </div>
                    )}
                  />
                );
              },
            }}
          />
        </FormInput>

        {currentError && shouldRevalidate && (
          <FormHelper
            helperText={helperText}
            error={{ ...currentError, message: currentError.message }}
            label={label}
          />
        )}
      </FormControl>
    </ErrorBoundary>
  );
};

DateInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.object,
  helperText: PropTypes.string,
  className: PropTypes.string,
  isGroupFieldInput: PropTypes.bool,
  groupFieldIndex: PropTypes.number,
  groupFieldName: PropTypes.string,
  datePickerOptions: PropTypes.object,
  shouldRevalidate: PropTypes.bool,
};

DateInput.defaultProps = {
  datePickerOptions: {},
  options: {},
};

export default DateInput;
